import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import { Badge } from "@/components/ui/badge";
import {
  FileText,
  CheckCircle,
  AlertTriangle,
  XCircle,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  Code,
} from "lucide-react";
import type { FileExtractionResult } from "@shared/discovery-types";

interface FileExtractionCardProps {
  result: FileExtractionResult;
  onReExtract?: (documentId: string) => void;
  showLLMDetails?: boolean;
}

const statusConfig = {
  success: {
    icon: CheckCircle,
    color: "text-success",
    bgColor: "bg-success/10",
    label: "Extracted",
  },
  partial: {
    icon: AlertTriangle,
    color: "text-warning",
    bgColor: "bg-warning/10",
    label: "Partial",
  },
  failed: {
    icon: XCircle,
    color: "text-destructive",
    bgColor: "bg-destructive/10",
    label: "Failed",
  },
};

const documentTypeLabels: Record<string, string> = {
  bank_statement: "Bank Statement",
  tax_filing: "Tax Filing",
  financial_statement: "Financial Statement",
  vat_form: "VAT Form",
  invoice: "Invoice",
  unknown: "Unknown",
};

export function FileExtractionCard({
  result,
  onReExtract,
  showLLMDetails = false,
}: FileExtractionCardProps) {
  const [isLLMOpen, setIsLLMOpen] = useState(false);

  const config = statusConfig[result.status];
  const StatusIcon = config.icon;

  const extractedFieldsCount = Object.keys(result.extractedFields).filter(
    key => {
      const value =
        result.extractedFields[key as keyof typeof result.extractedFields];
      return value !== undefined && value !== null;
    }
  ).length;

  return (
    <Card className="overflow-hidden">
      <CardHeader className={`py-3 px-4 ${config.bgColor}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileText className="h-5 w-5 text-muted-foreground" />
            <div>
              <CardTitle className="text-sm font-medium">
                {result.fileName}
              </CardTitle>
              <p className="text-xs text-muted-foreground">
                {documentTypeLabels[result.classification.type] ||
                  result.classification.type}
                {result.classification.subtype &&
                  ` (${result.classification.subtype})`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={config.color}>
              <StatusIcon className="h-3 w-3 mr-1" />
              {config.label}
            </Badge>
            <Badge variant="secondary">{result.confidence}%</Badge>
          </div>
        </div>
      </CardHeader>

      <CardContent className="p-4 space-y-4">
        {/* Extracted Fields Summary */}
        <div>
          <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">
            Extracted ({extractedFieldsCount} fields)
          </h4>
          <div className="flex flex-wrap gap-2">
            {result.extractedFields.companyInfo?.name && (
              <Badge
                variant="outline"
                className="text-success border-success/50"
              >
                Company: {result.extractedFields.companyInfo.name}
              </Badge>
            )}
            {result.extractedFields.companyInfo?.uid && (
              <Badge
                variant="outline"
                className="text-success border-success/50"
              >
                UID: {result.extractedFields.companyInfo.uid}
              </Badge>
            )}
            {result.extractedFields.taxInfo?.canton && (
              <Badge
                variant="outline"
                className="text-success border-success/50"
              >
                Canton: {result.extractedFields.taxInfo.canton}
              </Badge>
            )}
            {result.extractedFields.taxInfo?.taxYear && (
              <Badge
                variant="outline"
                className="text-success border-success/50"
              >
                Tax Year: {result.extractedFields.taxInfo.taxYear}
              </Badge>
            )}
            {result.extractedFields.bankAccounts &&
              result.extractedFields.bankAccounts.length > 0 && (
                <Badge
                  variant="outline"
                  className="text-success border-success/50"
                >
                  {result.extractedFields.bankAccounts.length} Bank Account(s)
                </Badge>
              )}
            {result.extractedFields.vendorsCount !== undefined &&
              result.extractedFields.vendorsCount > 0 && (
                <Badge
                  variant="outline"
                  className="text-success border-success/50"
                >
                  {result.extractedFields.vendorsCount} Vendors
                </Badge>
              )}
            {result.extractedFields.customersCount !== undefined &&
              result.extractedFields.customersCount > 0 && (
                <Badge
                  variant="outline"
                  className="text-success border-success/50"
                >
                  {result.extractedFields.customersCount} Customers
                </Badge>
              )}
            {result.extractedFields.transactionsCount !== undefined &&
              result.extractedFields.transactionsCount > 0 && (
                <Badge
                  variant="outline"
                  className="text-success border-success/50"
                >
                  {result.extractedFields.transactionsCount} Transactions
                </Badge>
              )}
          </div>
        </div>

        {/* Missing Fields */}
        {result.missingExpected.length > 0 && (
          <div>
            <h4 className="text-xs font-semibold uppercase text-muted-foreground mb-2">
              Missing ({result.missingExpected.length})
            </h4>
            <div className="flex flex-wrap gap-2">
              {result.missingExpected.map(field => (
                <Badge
                  key={field}
                  variant="outline"
                  className="text-destructive border-destructive/50"
                >
                  {field.replace(/\./g, " > ")}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Error Message */}
        {result.error && (
          <div className="p-2 bg-destructive/10 border border-destructive/30 rounded text-sm text-destructive">
            {result.error}
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-between pt-2 border-t">
          <div className="flex items-center gap-2">
            {showLLMDetails && result.llmDebug && (
              <Collapsible open={isLLMOpen} onOpenChange={setIsLLMOpen}>
                <CollapsibleTrigger asChild>
                  <Button variant="ghost" size="sm" className="text-xs">
                    <Code className="h-3 w-3 mr-1" />
                    LLM Details
                    {isLLMOpen ? (
                      <ChevronUp className="h-3 w-3 ml-1" />
                    ) : (
                      <ChevronDown className="h-3 w-3 ml-1" />
                    )}
                  </Button>
                </CollapsibleTrigger>
              </Collapsible>
            )}
          </div>
          {onReExtract && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => onReExtract(result.documentId)}
              className="text-xs"
            >
              <RefreshCw className="h-3 w-3 mr-1" />
              Re-Extract
            </Button>
          )}
        </div>

        {/* LLM Debug Details */}
        {showLLMDetails && result.llmDebug && (
          <Collapsible open={isLLMOpen} onOpenChange={setIsLLMOpen}>
            <CollapsibleContent className="space-y-2">
              <div className="p-3 bg-muted rounded-lg text-xs font-mono space-y-2">
                <div>
                  <span className="text-muted-foreground">Model:</span>{" "}
                  <span>{result.llmDebug.model}</span>
                </div>
                {result.llmDebug.tokensUsed && (
                  <div>
                    <span className="text-muted-foreground">Tokens:</span>{" "}
                    <span>{result.llmDebug.tokensUsed}</span>
                  </div>
                )}
                {result.llmDebug.extractionTimeMs && (
                  <div>
                    <span className="text-muted-foreground">Time:</span>{" "}
                    <span>{result.llmDebug.extractionTimeMs}ms</span>
                  </div>
                )}
                <div>
                  <span className="text-muted-foreground block mb-1">
                    Prompt:
                  </span>
                  <pre className="whitespace-pre-wrap text-xs bg-background p-2 rounded border max-h-40 overflow-auto">
                    {result.llmDebug.prompt}
                  </pre>
                </div>
                <div>
                  <span className="text-muted-foreground block mb-1">
                    Response:
                  </span>
                  <pre className="whitespace-pre-wrap text-xs bg-background p-2 rounded border max-h-40 overflow-auto">
                    {result.llmDebug.response}
                  </pre>
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      </CardContent>
    </Card>
  );
}
